// Central site content + constants for TRJ Services

export const SITE = {
  name: "TRJ Services",
  tagline: "Tax · Accounting · Business Advisory",
  motto: "Clarity. Strategy. Compliance. Growth. Legacy.",
  calendlyUrl: "https://calendly.com/trjservicesinc/consultation",
  consultationUrl: "https://calendly.com/trjservicesinc/consultation",
  phone: "708-794-8564",
  phoneHref: "tel:+17087948564",
  email: "contact@trjsvc.com",
  emailHref: "mailto:contact@trjsvc.com",
  address: "1818 Ridge Rd, Ste 7, Homewood, IL 60430",
  hours: "Mon – Fri · 9:00 AM – 6:00 PM CT",
  socials: {
    facebook: "https://www.facebook.com/TRJSVC",
    linkedin: "https://www.linkedin.com/in/trj-services9a2a12a",
    twitter: "https://x.com/ServicesTrj",
  },
  assets: {
    // Logo + founder bundled with the deploy (same-origin, no third-party CDN dependency,
    // cache-busted by the build, reliable on mobile / iOS Safari).
    logo: "/img/logo.png",
    founder: "/img/founder.jpg",
    boardroom: "https://images.pexels.com/photos/16859955/pexels-photo-16859955.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    officeModern: "https://images.pexels.com/photos/34736064/pexels-photo-34736064.png?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    team: "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    meeting: "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    ministry: "https://images.unsplash.com/photo-1519491050282-cf00c82424b4?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
  },
};

export const SERVICES = [
  {
    slug: "individual",
    icon: "User",
    title: "Individual Services",
    tagline: "Personal Tax & Financial Support",
    description:
      "Helping individuals navigate tax preparation, amendments, IRS notices, and proactive tax planning with confidence and clarity.",
    items: [
      "Individual Tax Preparation",
      "Tax Amendments",
      "IRS Notice Assistance",
      "Tax Planning & Consultation",
    ],
  },
  {
    slug: "business",
    icon: "Building2",
    title: "Business Services",
    tagline: "Financial Solutions for Growing Businesses",
    description:
      "Supporting business owners with the financial reporting, tax compliance, and operational support needed to manage and grow their organizations.",
    items: [
      "Business Tax Returns",
      "Bookkeeping Services",
      "Accounting Support",
      "Payroll Assistance",
    ],
  },
  {
    slug: "nonprofit",
    icon: "Handshake",
    title: "Nonprofit Services",
    tagline: "Compliance & Stewardship Support",
    description:
      "Helping nonprofits and ministries establish, maintain, and protect their organizations through proper compliance and governance practices.",
    items: [
      "501(c)(3) Formation",
      "Compliance Reviews",
      "Form 990 Filings",
      "Board Governance Support",
    ],
  },
  {
    slug: "infrastructure",
    icon: "Layers",
    title: "Business Foundation Services",
    tagline: "Building Strong Foundations for Success",
    description:
      "Providing the structure, systems, and strategic guidance organizations need to operate efficiently, remain compliant, and position themselves for long-term growth.",
    items: [
      "Business Formation",
      "Compliance Management",
      "Strategic Advisory",
      "Operational Structure & Planning",
    ],
  },
];

export const FRAMEWORK_LEVELS = [
  {
    level: 1,
    title: "Financial Clarity Snapshot™",
    description:
      "Identify strengths, weaknesses, and opportunities through a guided diagnostic of your current financial position.",
    icon: "Compass",
    cta: {
      type: "book",
      label: "Book Snapshot",
      href: "https://calendly.com/trjservicesinc/financialclaritysnapshot",
    },
  },
  {
    level: 2,
    title: "72-Hour Financial Cleanup™",
    description:
      "A rapid financial review designed to identify immediate concerns, uncover potential issues, and provide recommendations based on the first three months of business bank statements.",
    icon: "Zap",
    badge: "Rapid · 72 Hours",
    cta: {
      type: "pay",
      label: "Get Started",
      href: "https://www.paypal.com/ncp/payment/PN8LHUBHXZ9FG",
    },
  },
  {
    level: 3,
    title: "Financial Cleanup Intensive™",
    description:
      "Restore order and correct financial issues — bookkeeping reconstruction, compliance gaps, reporting cleanup.",
    icon: "Wrench",
    cta: { type: "consult" },
  },
  {
    level: 4,
    title: "Financial Oversight Retainer™",
    description:
      "Maintain accountability and ongoing monitoring with monthly oversight, advisory, and reporting cadence.",
    icon: "ShieldCheck",
    cta: { type: "consult" },
  },
  {
    level: 5,
    title: "Executive Financial Command™",
    description:
      "Executive-level oversight, compliance leadership, and strategic direction for organizations operating at scale.",
    icon: "Crown",
    premium: true,
    cta: { type: "consult" },
  },
];

export const WHO_WE_SERVE = [
  { label: "Individuals", icon: "User" },
  { label: "Business Owners", icon: "Briefcase" },
  { label: "Nonprofits", icon: "HeartHandshake" },
  { label: "Churches & Ministries", icon: "Church" },
  { label: "Entrepreneurs", icon: "Rocket" },
  { label: "Professional Service Providers", icon: "Users" },
];

export const WHY_TRJ = [
  { label: "25+ Years Serving Families & Businesses", icon: "Award" },
  { label: "Multi-Business Founder", icon: "Building2" },
  { label: "Financial Strategist", icon: "TrendingUp" },
  { label: "Tax Professional", icon: "FileText" },
  { label: "Business Consultant", icon: "Briefcase" },
  { label: "Nonprofit Advisor", icon: "HeartHandshake" },
  { label: "Ministry Leader", icon: "Church" },
  { label: "Year-Round Support", icon: "CalendarCheck" },
];

export const CLIENT_JOURNEY = [
  { step: 1, title: "Schedule Consultation", description: "Book a focused conversation to share your situation and goals." },
  { step: 2, title: "Financial Assessment", description: "We review your current systems, records, and compliance posture." },
  { step: 3, title: "Strategic Recommendations", description: "You receive a clear, prioritized plan tailored to your organization." },
  { step: 4, title: "Implementation", description: "We help execute — cleanup, structure, filings, and reporting." },
  { step: 5, title: "Ongoing Support", description: "Continued oversight, advisory, and executive financial leadership." },
];

export const PRODUCTS = [
  {
    slug: "business-startup-toolkit",
    title: "Business Startup Toolkit™",
    price: "297",
    description: "Everything entrepreneurs need to start, launch, and build a strong foundation for long-term success.",
    image: "/img/product-business-startup.jpg",
    paypalUrl: "https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=NM58ME24FEULA",
    includes: ["Business Startup Guide","New Business Startup Checklist","Business Formation Worksheet","Compliance Calendar","First-Year Planning Sheet"],
  },
  {
    slug: "financial-foundation-bundle",
    title: "Financial Foundation Bundle™",
    price: "197",
    description: "Build a strong financial foundation — create clarity, stay organized, and make better decisions.",
    image: "/img/product-financial-foundation.jpg",
    paypalUrl: "https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=Z87AHM5NBGQBY",
    includes: ["Financial Document Organizer","Monthly Expense Tracker","Financial Goal Planning Workbook","Monthly Financial Health Review Checklist","Year-End Financial Review Checklist"],
  },
  {
    slug: "entrepreneur-success-bundle",
    title: "Entrepreneur Success Bundle™",
    price: "397",
    description: "Everything you need to start, stay organized, and grow your business with confidence.",
    image: "/img/product-entrepreneur.jpg",
    paypalUrl: "https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=RX8658945KN6A",
    includes: ["Business Startup Guide","Business Startup Checklist","Business Monthly Expense Tracker","Business Tax Preparation Checklist","Business Growth Planning Checklist"],
    badge: "Bestseller",
  },
  {
    slug: "nonprofit-leadership-bundle",
    title: "Nonprofit Leadership Bundle™",
    price: "297",
    description: "Strengthen governance, ensure compliance, maximize impact, and build a stronger future.",
    image: "/img/product-nonprofit.jpg",
    paypalUrl: "https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=NDK99UCXTAMYW",
    includes: ["Nonprofit Governance Guide","Nonprofit Compliance Checklist","Financial Goal Planning Workbook","Year-End Financial Review Checklist"],
  },
];

export const COURSES = [
  {
    slug: "tax-prep-fundamentals",
    title: "Tax Preparation Fundamentals",
    price: "$699.95",
    pill: "Foundation Course",
    paypalUrl: "https://www.paypal.com/ncp/payment/8RYTJ6UKR57LG",
    description:
      "Learn the fundamentals of preparing individual tax returns with confidence, accuracy, and professional ethics.",
    includes: [
      "Individual tax returns",
      "Filing status",
      "Credits & deductions",
      "Ethics basics",
      "Client intake process",
    ],
  },
  {
    slug: "starting-a-tax-business",
    title: "Starting a Tax Business",
    price: "$1,999",
    pill: "Business Track",
    paypalUrl: "https://www.paypal.com/ncp/payment/TQPTKCENSWSCC",
    applicationUrl: "/tax-school/apply",
    applicationEmbedUrl: "https://docs.google.com/forms/d/e/1FAIpQLSf41PmAKmnd3tLSctgpH_oWvvKX4JV12cUO9xHG_qMB2SjiYA/viewform?usp=sharing&ouid=103515936579019370754",
    description:
      "A practical roadmap to launching, structuring, and growing your own tax preparation business.",
    includes: [
      "Business setup",
      "Pricing",
      "Marketing",
      "Client management",
      "Operations",
    ],
  },
  {
    slug: "trj-certification-bundle",
    title: "TRJ Tax Professional Certification Program",
    price: "$2,695",
    pill: "Bundle · Save $4",
    paypalUrl: "https://www.paypal.com/ncp/payment/GPK5XDR4UZMJS",
    description:
      "Our complete certification track — combining Tax Fundamentals and Tax Business Startup with ethics training, templates, and a Certificate of Completion.",
    includes: [
      "Tax Fundamentals",
      "Tax Business Startup",
      "Ethics",
      "Templates",
      "Checklists",
      "Certificate of Completion",
    ],
    bundle: true,
  },
];

export const RESOURCES = {
  articles: [
    { title: "Financial Organization: The Foundation of Long-Term Success", description: "Learn why strong financial systems are essential for individuals, businesses, nonprofits, and ministries seeking stability and growth." },
    { title: "Common Tax Mistakes That Cost Individuals and Businesses Money", description: "Discover the most common filing and compliance errors and practical ways to avoid them." },
    { title: "When Is It Time to Move Beyond Basic Bookkeeping?", description: "Explore the signs that your organization may need accounting support, financial oversight, or strategic guidance." },
    { title: "Understanding the Difference Between Tax Preparation and Tax Planning", description: "Learn how proactive planning can help reduce surprises and support better financial decision-making." },
    { title: "Why Compliance Matters for Nonprofits and Ministries", description: "Understand the importance of maintaining compliance, protecting tax-exempt status, and meeting reporting requirements." },
    { title: "Building a Strong Financial Foundation for Business Growth", description: "Learn how financial structure, reporting, and planning contribute to sustainable business success." },
  ],
  "tax-updates": [
    { title: "Annual Tax Filing Deadlines Every Taxpayer Should Know", description: "Stay informed about key filing dates for individuals, businesses, nonprofits, and organizations." },
    { title: "IRS Notice Received? Here's What To Do Next", description: "Learn the first steps to take when you receive correspondence from the IRS." },
    { title: "Important Tax Law Changes Affecting Small Businesses", description: "A summary of significant updates that may impact business owners and entrepreneurs." },
    { title: "Recordkeeping Requirements for Individuals and Businesses", description: "Understand what documents to keep and how long to retain important records." },
    { title: "Estimated Tax Payment Guidelines", description: "Helpful information for taxpayers who may be required to make quarterly estimated payments." },
    { title: "Nonprofit Filing Requirements and Compliance Reminders", description: "An overview of annual filing obligations and reporting deadlines." },
  ],
  checklists: [
    { title: "Monthly Financial Health Review Checklist", description: "Monitor financial performance, reporting, expenses, and key business indicators.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/67vf21lj_Monthly%20Financial%20Health%20Review%20Checklist.pdf" },
    { title: "Business Growth Planning Checklist", description: "Evaluate systems, staffing, finances, and strategic goals before scaling operations.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/rud7ize1_Business%20Growth%20Planning%20Checklist.pdf" },
  ],
  downloads: [
    { title: "Financial Document Organizer.pdf", description: "A guide for organizing important financial records and tax documents.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/uluask5b_TRJ%20SERVICES%20FINANCIAL%20DOCUMENT%20ORGANIZER%20.pdf" },
    { title: "Business Startup Guide.pdf", description: "An introductory guide covering business formation, compliance, and operational planning.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/84xe9q16_TRJ%20SERVICES%20BUSINESS%20STARTUP%20GUIDE%20.pdf" },
    { title: "Tax Preparation Checklist.pdf", description: "A resource to help individuals and businesses prepare for tax filing season.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/6jz56265_TRJ%20SERVICES%20TAX%20PREPARATION%20CHECKLIST%20.pdf" },
    { title: "Monthly Expense Tracker.xlsx", description: "A worksheet designed to help monitor and categorize business expenses.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/0m2imghh_TRJ%20Monthly%20Expense%20Tracker.xlsx" },
    { title: "Financial Goal Planning Workbook.pdf", description: "A workbook for setting financial objectives and tracking progress throughout the year.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/gwtsg4sr_TRJ%20Services%20Financial%20Goal%20Planning%20Workbook.pdf" },
    { title: "Nonprofit Governance Guide.pdf", description: "A resource outlining best practices for nonprofit leadership, compliance, and board responsibilities.", fileUrl: "https://customer-assets.emergentagent.com/job_trj-services-demo/artifacts/edt0ta0n_TRJ%20SERVICES%20NONPROFIT%20GOVERNANCE%20GUIDE%20.pdf" },
  ],
};
